import { Link } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Sparkles, Link2 } from "lucide-react";
import { useBranding } from "@/lib/branding";

export default function LandingPage() {
  const { logoSrc, branding } = useBranding();
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoSrc ? (
              <img src={logoSrc} alt={branding?.company_name || ""} className="h-12 object-contain" />
            ) : (
              <div className="h-7 w-7 bg-foreground" />
            )}
            <span className="font-mono text-sm tracking-tight">EARLY YEARS ACADEMY</span>
          </div>
          <Link
            to="/login"
            data-testid="nav-admin-login"
            className="font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border border-border hover:bg-foreground hover:text-background transition-colors"
          >
            Admin Login →
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="eyebrow mb-6">01 / Continuing Professional Development</div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Deliver CPD courses.<br />
              <span className="text-muted-foreground">Track candidates.</span><br />
              Issue branded certificates.
            </h1>
            <p className="mt-8 max-w-xl text-base text-muted-foreground leading-relaxed">
              A focused platform for digital training providers. Build courses with video,
              text and PDF — send a private magic link to each candidate — auto-generate a
              certificate with your logo the moment they pass.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/login"
                data-testid="hero-get-started"
                className="inline-flex items-center gap-2 bg-foreground text-background px-6 py-3 text-sm font-medium tracking-tight hover:bg-foreground/90 transition-colors"
              >
                Open admin <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center gap-2 border border-border px-6 py-3 text-sm font-medium hover:bg-secondary transition-colors"
              >
                How it works
              </a>
            </div>
          </div>
          <div className="hidden lg:flex col-span-4 items-end justify-end">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground text-right">
              <div>EST. 2026</div>
              <div className="mt-1">VERSION 1.0</div>
              <div className="mt-6 h-32 w-px bg-border ml-auto" />
              <div className="mt-2">A FOCUSED LMS</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-3">
          {[
            { icon: BookOpen, n: "02", title: "Mixed-media courses", body: "Stack video, text and PDF modules in any order. Configure your quiz pass rate per course." },
            { icon: Link2, n: "03", title: "Private magic links", body: "Create a candidate by name and email. A unique link unlocks the course — no password required." },
            { icon: Award, n: "04", title: "Branded certificates", body: "On completion (modules viewed + quiz passed) a PDF certificate is generated with your logo and brand colours." },
          ].map(({ icon: Icon, n, title, body }, i) => (
            <div key={i} className={`p-10 ${i < 2 ? "md:border-r border-border" : ""} border-b md:border-b-0 last:border-b-0`}>
              <div className="flex items-center justify-between mb-8">
                <Icon size={28} strokeWidth={1.5} />
                <span className="font-mono text-xs text-muted-foreground">/ {n}</span>
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-3">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="eyebrow text-background/60 mb-3">/ 05 — Get started</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">
              Your training programme, online by tomorrow.
            </h2>
          </div>
          <Link
            to="/login"
            data-testid="cta-admin-login"
            className="inline-flex items-center gap-3 bg-background text-foreground px-8 py-4 font-medium tracking-tight hover:bg-background/90 transition-colors"
          >
            <Sparkles size={16} strokeWidth={1.5} />
            Sign in as admin
          </Link>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between font-mono text-[10px] tracking-widest text-muted-foreground">
          <span>EARLY YEARS ACADEMY · A FOCUSED LMS</span>
          <span>© 2026</span>
        </div>
      </footer>
    </div>
  );
}
